/* eslint-disable max-lines */
import { GenericShopApiResponse } from '../../../common/apiAdapters/types';
import { DataFetchingState } from '../dataFetching/generators';
import { Dictionary } from '../../../../types/globals';
import { ProductReviewsState } from '../productReviews/types';
import { sortSimplesByAvailability } from './helper';
import { FabricSampleCountry, FabricSampleState } from './fabricSample';

export enum VideoSliderPosition {
    FIRST = 'first',
    SECOND = 'second',
    LAST = 'last',
}

export interface GalleryState {
    slides: Array<Slide>;
    currentIndex: number;
}

export enum ProductImageTag {
    REGULAR = 'reg',
    DETAIL = 'dtl',
    SIZE_RELATION = 'srl',
    INFO_GRAPHIC = 'inf',
    LOOK = 'lok',
    USER_GENERATED_CONTENT = 'ugc',
    MOOD = 'mod',
}

export const GalleryTrackingItemMapping = {
    [ProductImageTag.REGULAR]: 'REGULAR',
    [ProductImageTag.DETAIL]: 'DETAIL',
    [ProductImageTag.SIZE_RELATION]: 'SIZE RELATION',
    [ProductImageTag.INFO_GRAPHIC]: 'INFO GRAPHIC',
    [ProductImageTag.LOOK]: 'LOOKS',
    [ProductImageTag.USER_GENERATED_CONTENT]: 'USER GENERATED CONTENT',
    [ProductImageTag.MOOD]: 'MOOD IMAGE',
    video: 'VIDEO',
    twomh: 'TWO MAN HANDLING',
    blank: 'blank',
};

export interface ColorVariants {
    isSelected: boolean;
    colorImagePath: string;
    colorImageTag: ProductImageTag;
    colorImageUrl: string;
    fabricSampleName: string;
    fabricSampleImage: string;
    enableFabricSample: boolean;
    showFabricSampleInForm: boolean;
    pdpUrl: string;
    sku: string;
    configSku: string;
}

export interface ColorVariantsState {
    data: ColorVariants[];
    didInvalidate: boolean;
    identifier: string;
    isFetching: boolean;
}

export interface TrackingActionType {
    videoLoad: boolean;
    videoPlay: boolean;
    videoPercent: number;
}

export enum TrackingVideoActions {
    videoLoad = 'videoLoad',
    videoPlay = 'videoPlay',
    videoPercent = 'videoPercent',
}

export interface UserActionsType {
    selectedQuantity: number;
}

export interface ColorHoverState {
    imageTag: ProductImageTag;
    isHover: boolean;
    imagePath: string;
}

export interface ProductDetailState extends DataFetchingState {
    colorHover: ColorHoverState;
    colorVariants: ColorVariantsState;
    gallery: GalleryState;
    pdpApiData?: PDPApiProductData;
    productdetail: ProductDetail;
    reviews: ProductReviewsState;
    simpleSkuIdentifier?: string;
    trackingActions: TrackingActionType;
    userActions: UserActionsType;
    invalidSimpleSku?: boolean;
    orderFabricSample: FabricSampleState;
}

export interface ProductBrandDetails {
    name: string;
    urlKey: string;
    description: string;
}

export interface ProductCategoryDetails {
    id: number;
    name: string;
    urlKey: string;
}

export enum SlideVaration {
    IMG,
    VID,
    TWOMH,
    BLANK,
}

export interface SliderVideo {
    slideType: SlideVaration.VID;
    url: string;
    nativeUrl?: string;
    type?: string;
    thumb?: string;
    thumbWithIcon?: string;
    tag: 'video';
}

export interface SliderImage {
    slideType: SlideVaration.IMG;
    altTag: string;
    seoUrl: string;
    thumb: string;
    big: string;
    zoom: string;
    path: string;
    tag: ProductImageTag;
}

interface SliderTwoManHandlingSlide {
    slideType: SlideVaration.TWOMH;
    tag: 'twomh';
}

interface SliderBlankSlide {
    slideType: SlideVaration.BLANK;
    tag: 'blank';
}

export type Slide = SliderImage | SliderVideo | SliderTwoManHandlingSlide | SliderBlankSlide;

export interface APIProductImages {
    /* eslint-disable @typescript-eslint/camelcase, camelcase */
    alt_tag: string;
    seo_url: string;
    /* eslint-enable @typescript-eslint/camelcase, camelcase */
    thumb: string;
    big: string;
    zoom: string;
    path: string;
    tag: ProductImageTag;
}

export interface InnerDetails {
    careLabel?: string;
    color?: string;
    material?: string;
    measures?: string;
    details?: string;
}

export interface ProductRatings {
    average: number;
    count: number;
}

export interface PdpSeoData {
    seoTitle: string;
    seoDescription: string;
    availableIn: Dictionary<string>;
}

export interface VideoRaw {
    /* eslint-disable @typescript-eslint/camelcase, camelcase */
    source?: string;
    external_url?: string;
    cover_with_play?: string;
    cover?: string;
    type?: string;
    video_position?: VideoSliderPosition;
    video_position_position?: string;
    /* eslint-enable @typescript-eslint/camelcase, camelcase */
}

export type ProductSimpleVideoResponse = GenericShopApiResponse<VideoRaw>;

export interface ProductDetail {
    activeSimpleSKU: string;
    blackFriday: boolean;
    additionalInfo?: string;
    brand?: ProductBrandDetails;
    category?: ProductCategoryDetails;
    cyberMonday: boolean;
    deliveryInfo: DeliveryInfo | null;
    hasAddonProducts: boolean;
    imageUrl: string;
    innerDetails: InnerDetails;
    isAvailable: boolean;
    isBisrEnabled: boolean;
    isBisrSubscribed: boolean;
    name: string;
    numericPrice: number;
    originalPrice: string;
    price: string;
    quantityBadge?: number;
    rating?: ProductRatings;
    sizes: Size[];
    sku: string;
    selectedSize: string;
    seoData: PdpSeoData;
    uvpText?: string;
    isSustainable: boolean;
    sustainabilityInfo?: {
        title?: string;
        text?: string;
    };
    showBestsellerImage: boolean;
    quantity: number;
    fabricSampleCountries: FabricSampleCountry[];
    assemblyServiceProduct: PDPAssemblyService;
    isMoodImageFirstImage: boolean;
    // idCatalogConfig: string;
    // isVisible: boolean;
    // imagePath: string;
    // pdpUrl: string;
    // enableZoomBubble: boolean;
    // color: string[];
    // styles: string[];
    // altTag: string;
    // valuePropositions: PDPValueProposition[];
}

/* eslint-disable @typescript-eslint/camelcase, camelcase */
export interface ApiColorVariants {
    sku: string;
    pdp_url: string;
    images: {
        original: string;
    };
    fabric_sample_name?: string;
    fabric_sample_image?: string;
    enable_fabric_sample: boolean;
    show_in_fabric_sample_form: boolean;
}
/* eslint-disable @typescript-eslint/camelcase, camelcase */
export type PDPApiResponse = GenericShopApiResponse<PDPApiProductData>;

export type ColorVariantsAPIResponse = GenericShopApiResponse<ApiColorVariants[]>;

/* eslint-disable @typescript-eslint/camelcase, camelcase */
export interface Simple {
    price: number;
    original_price: number;
    available: boolean;
    is_visible: boolean;
    image_url: string;
    filter_size: string;
    size: string;
    delivery_time: number;
    quantity: number;
    installments_monthly_price?: number;
    installments_term?: number;
    is_special_sale_sold_out: boolean;
    alt_tag: string;
    sku: string;
    slider: APIProductImages[];
    slider_images: APIProductImages[];
    delivery_info: ApiDeliveryInfo | {};
    product_details: {
        pdp_care_label?: string;
        pdp_color?: string;
        pdp_material?: string;
        pdp_measures?: string;
        pdp_details?: string;
    };
    color_variants: ApiColorVariants[];
    bisr_enabled: boolean;
    has_addon_product?: boolean;
    is_user_subscribed: boolean;
    video: VideoRaw;
}
/* eslint-enable @typescript-eslint/camelcase, camelcase */

interface PDPValueProposition {
    type: string;
    image: {
        path: string;
        width: number;
        height: number;
    };
}

export interface RawSimples {
    [key: string]: {
        meta: Simple;
    };
}

/* eslint-disable @typescript-eslint/camelcase, camelcase */
export interface PDPApiProductData {
    meta: {
        sku: string;
        name: string;
        brand: string;
        uvp_text?: string;
        is_sustainable: boolean;
        show_bestseller_image: boolean;
        black_friday_top_deal: boolean;
        cyber_monday_top_deal: boolean;
        quantity_badge_number?: number;
        pdp_url: string;
        image_url: string;
        additional_info?: string;
        image_path?: string;
        enable_zoom_bubble?: boolean;
        colors?: string[];
        materials?: string[];
        styles?: string[];
        alt_tag?: string;
        categories?: string[];
        available_in?: { [key: string]: string };
        ratings: {
            average_score?: number;
            total_ratings?: number;
        };
        id_catalog_config?: string;
        is_visible?: boolean;
        value_propositions: PDPValueProposition[];
        assembly_service_sku?: string;
        show_mood_image_first: boolean;
        sustainability_info?: {
            title?: string;
            text?: string;
        };
    };
    simples: RawSimples;
    assemblyServiceProducts: PDPApiAssemblyServiceProduct[];
}
/* eslint-disable @typescript-eslint/camelcase, camelcase */

export interface PDPAssemblyService {
    price: string;
    sku: string;
    name: string;
    selected: boolean;
}

export interface PDPApiAssemblyServiceProduct {
    sku: string;
    name: string;
    price: {
        value: number;
        currency: string;
    };
    size: string;
}

export type SimplesObjectKey = PDPApiProductData['simples'];

export type SimplesArray = ReturnType<typeof sortSimplesByAvailability>;

export interface Size {
    filterSize: string;
    isAvailable: boolean;
    isVisible: boolean;
    simple: string;
    price: string;
    quanity: number;
}

export interface ApiDeliveryInfo {
    delivery_min_days: number;
    delivery_max_days: number;
    detailed_text: string;
    delivery_type: string;
    enable_xmas_delivery: boolean;
}

export interface DeliveryInfo {
    deliveryMaxDays: number;
    deliveryMinDays: number;
    detailedText: string;
    isDeliveryBeforeChristmas: boolean;
    isGenericInfo: boolean;
    label: string;
    twoManHandling: boolean;
}

export interface PdpRequestQueryParam {
    simpleSku?: string;
}
